// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 *
 * <HeaderClickable
 *      iconSrc={icon}
 *      onClick={() => !isMobile && history.push(cartUrl ?? "")}
 *      iconProps={{ type: "miniCart" }}
 *      clickableProps={{ "data-test-selector": "cartLink" }}
 *      titleProps={{
 *          "data-test-selector": totalCountDisplay ? "cartLinkQuantity" : "cartLinkSubTotal",
 *      }}
 *      >
 *          <>
 *              {`${translate("Top Message")}`}
 *              <Typography className="sub-title">{`${translate("Bottom Message")}`}</Typography>
 *          </>
 *      </HeaderClickable>
 */

import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps, IconProps } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { xScreen } from "@stanion/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

interface HeaderClickableProps {
    variant: "link" | "menu";
    iconProps?: IconProps;
    isOpen?: boolean;
}

type Props = HeaderClickableProps & ClickableProps;

interface HeaderClickableStyle {
    clickable?: ClickablePresentationProps;
    icon?: IconPresentationProps;
    mobileIcon?: IconPresentationProps;
    text?: TypographyProps;
    arrowIcon?: IconPresentationProps;
}

const styles: HeaderClickableStyle = {
    clickable: {
        css: css`
            display: flex;
            column-gap: 1px;
            align-items: center;
            justify-content: flex-start;
        `,
    },
    icon: {
        size: 30,
        color: "common.ui8",
        css: css`
            font-weight: bold;
            padding: 6px;

            ${xScreen("lte", "md")} {
                &,
                & > svg {
                    width: 32px;
                    height: 32px;
                }
            }

            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
    mobileIcon: {
        size: 30,
        color: "common.ui8",
        css: css`
            font-weight: bold;
            padding: 6px;

            ${xScreen("gt", "md")} {
                display: none;
            }
        `,
    },
    arrowIcon: {
        size: 12,
        color: "common.ui4",
        css: css`
            margin-left: 8px;

            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
    text: {
        size: 14,
        weight: "bold",
        color: "text.main",
        ellipsis: true,
        transform: "capitalize",
        css: css`
            letter-spacing: 0;
            line-height: 18px;
            display: flex;
            flex-direction: column;
            row-gap: 3px;

            & > ${TypographyStyle} {
                font-size: 12px;
                font-weight: normal;
                letter-spacing: 0;
                line-height: 13px;
            }

            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
};

const HeaderClickable: FC<Props> = ({ variant, isOpen, iconProps, children, ...otherProps }) => {
    if (variant === "menu") {
        return (
            <Clickable {...styles.clickable} {...otherProps}>
                <Typography {...styles.text}>{children}</Typography>
                <Icon src={ChevronDown} {...styles.icon} className="arrow-menu-icon" />
                <Icon {...iconProps} {...styles.mobileIcon} className="arrow-menu-icon" />
            </Clickable>
        );
    }

    return (
        <Clickable {...styles.clickable} {...otherProps}>
            <Typography {...styles.text}>{children}</Typography>
        </Clickable>
    );
};

export default HeaderClickable;
